import { Box } from '@mui/system';
import { isEmpty } from 'lodash';
import React from 'react';
import { Note } from 'sr-types/lib/search/v1/graphql';
import { SECURITY_ACTIONS, useSecurity } from '../../../auth/Security';
import Confirm from '../../../components/Confirm';
import Error from '../../../components/Error';
import Loading from '../../../components/Loading';
import NotFound from '../../../components/NotFound';
import { DeepLinkItemHighlight } from '../../../deepLink';
import { ScrollableLayout } from '../../../Layout';
import { useNotesContext } from '../hooks/useNotesContext';
import CreateNoteForm from './dataInput/CreateNoteForm';
import NotesSearchInput from './dataInput/NotesSearchInput';
import NotesTagsFilter from './dataInput/NotesTagsFilter';
import NoteManager from './NoteManager';

type NotesManagerProps = {
    noteFetchError: unknown;
    hideSearchAndEditor: boolean;
    showOnlyEditor: boolean;

    term: string;
    setTerm: (value: string) => void;
    availableTags: string[];
    selectedTags: string[];
    setSelectedTags: (tags: string[]) => void;
    t: (key: string) => string;
    emptyNoteContent: string;
    resetEditor: boolean;
    onNewNoteEditorChange: (content: string, title: string) => void;
    newNoteDraft: Note | null;
    onTagChangeHandlerForNewNote: (name: string, value: string[]) => void;
    onSaveNewNote: () => void;
    onCancelNewNote: () => void;
    disableSaveForNewNote: boolean;
    onNewNoteShareClick: () => void;
    onExistingNoteShareClick: (noteId: string) => void;
    showNotes: boolean;
    loading: boolean;
    filteredNotes: Note[];
    highlightedNoteId?: string;
    highlightedNoteRef: React.RefObject<HTMLDivElement>;
    editingNote: Note | null;
    collapsed: boolean;
    setEditingNote: (note: Note) => void;
    isInEditMode: boolean;
    setIsInEditMode: (isInEditMode: boolean) => void;
    onExistingNoteChange: (note: Note, content: string, title: string) => void;
    onTagChangeHandlerForExistingNote: (name: string, value: string[], note: Note) => void;
    onSaveExistingNote: (note: Note) => void;
    onCancelExistingNote: () => void;
    onDeleteHandle: (noteId: string) => Promise<unknown>;
    onAssociateStatusNote?: {
        saveFunction?: (note: Note) => void;
        loadingState?: boolean;
        statusNote?: Note;
        deletedStatusNoteId?: string;
        onStatusNoteChanged?: (note: Note | null, productionId: string) => void;
        onStatusNoteDeleted?: () => void;
        isStatusNoteAction?: boolean;
    };
    getNoteSx: (noteId: string) => object;
    addPendingNoteAction: (tempId: string, cb: (noteId: string) => void) => void;
    deleteNoteId?: string;
    onConfirmDelete: () => void;
    onCancelDelete: () => void;
    isDeleting: boolean;
    /** Incrementing this value re-triggers auto-focus in the new-note editor */
    focusTrigger?: number;
};

export default function NotesManager({
    noteFetchError,
    hideSearchAndEditor,
    showOnlyEditor,
    term,
    setTerm,
    availableTags,
    selectedTags,
    setSelectedTags,
    t,
    emptyNoteContent,
    resetEditor,
    onNewNoteEditorChange,
    newNoteDraft,
    onTagChangeHandlerForNewNote,
    onSaveNewNote,
    onCancelNewNote,
    disableSaveForNewNote,
    onNewNoteShareClick,
    onExistingNoteShareClick,
    showNotes,
    loading,
    filteredNotes,
    highlightedNoteId,
    highlightedNoteRef,
    editingNote,
    collapsed,
    setEditingNote,
    isInEditMode,
    setIsInEditMode,
    onExistingNoteChange,
    onTagChangeHandlerForExistingNote,
    onSaveExistingNote,
    onCancelExistingNote,
    onDeleteHandle,
    onAssociateStatusNote,
    getNoteSx,
    addPendingNoteAction,
    deleteNoteId,
    onConfirmDelete,
    onCancelDelete,
    isDeleting,
    focusTrigger = 0
}: NotesManagerProps) {
    const { entity } = useNotesContext();
    const { allowed: canCommentOnParentEntity } = useSecurity(SECURITY_ACTIONS.COMMENT, {
        entityName: entity
    });

    if (noteFetchError) {
        return <Error error={noteFetchError} />;
    }

    return (
        <ScrollableLayout sx={{ pr: 1, height: '100%' }}>
            {!hideSearchAndEditor && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 1, width: '100%' }}>
                    {!showOnlyEditor && (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <NotesSearchInput id={'notes-search-input'} value={term} onChange={setTerm} />
                            <NotesTagsFilter
                                availableTags={availableTags}
                                selectedTags={selectedTags}
                                onChange={setSelectedTags}
                            />
                        </Box>
                    )}
                    {canCommentOnParentEntity && (
                        <CreateNoteForm
                            editorId={'new-note'}
                            content={emptyNoteContent}
                            resetEditor={resetEditor}
                            onChange={onNewNoteEditorChange}
                            tagsValue={newNoteDraft?.labels}
                            onTagsChange={onTagChangeHandlerForNewNote}
                            onSave={onSaveNewNote}
                            onCancel={onCancelNewNote}
                            onShare={onNewNoteShareClick}
                            disableSave={disableSaveForNewNote}
                            focusTrigger={focusTrigger}
                        />
                    )}
                </Box>
            )}

            {!showNotes ? null : loading ? (
                <Loading />
            ) : filteredNotes?.length ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1, width: '100%' }}>
                    {filteredNotes.map((note) => (
                        <DeepLinkItemHighlight key={note.id} id={note.id}>
                            <NoteManager
                                ref={note.id === highlightedNoteId ? highlightedNoteRef : null}
                                note={note}
                                editingNote={editingNote}
                                collapsed={collapsed}
                                onEditNote={(currentNote) => {
                                    setEditingNote(currentNote);

                                    if (!isInEditMode) {
                                        setIsInEditMode(true);
                                    }
                                }}
                                showNotesTags={
                                    isInEditMode &&
                                    (editingNote?.id === note.id || editingNote?.reference?.id === note.reference?.id)
                                }
                                onChange={(content, title) => onExistingNoteChange(note, content, title)}
                                onTagChangeHandler={(name, value) =>
                                    onTagChangeHandlerForExistingNote(name, value, note)
                                }
                                onSave={() => onSaveExistingNote(note)}
                                onCancelNote={onCancelExistingNote}
                                onDelete={(noteId: string) => onDeleteHandle(noteId)}
                                onShareClick={onExistingNoteShareClick}
                                onAssociateStatusNote={onAssociateStatusNote}
                                sx={getNoteSx(note.id)}
                                addPendingNoteAction={addPendingNoteAction}
                                disableAllActions={isInEditMode && editingNote?.id !== note.id}
                            />
                        </DeepLinkItemHighlight>
                    ))}
                </Box>
            ) : (
                <NotFound text={t('notes.not.found')} />
            )}

            <Confirm
                open={Boolean(deleteNoteId) && !isEmpty(deleteNoteId)}
                title={t('notes.delete')}
                body={t('notes.delete.message')}
                onConfirm={onConfirmDelete}
                onCancel={onCancelDelete}
                isLoading={isDeleting}
            />
        </ScrollableLayout>
    );
}
